import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SupportService } from '../services/support.service';
import { SpinnerService } from 'src/app/shared/services';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { EnergyCreditsAdminService } from '../energy-credits-admin-facilities/services/energy-credits-admin.service';
import { FacilityEnergyCredit } from '../energy-credits-admin-facilities/models/facility-energy-credits.model';
import { SubmissionsService } from 'src/app/modules/energy-credits/services/submissions.service';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';

import { finalize, Subject, takeUntil } from 'rxjs';
import {
  MatRow,
  MatCell,
  MatHeaderRow,
  MatHeaderCell,
  MatTableModule,
} from '@angular/material/table';

const REFRESH_RETAILER_FAIL_MESSAGE =
  'An unexpected error occurred while refreshing the Retailer. The submission was not changed.';
const RESYNC_FACILITY_FAIL_MESSAGE =
  'An unexpected error occurred while resyncing the facility.';
const MIGRATE_CERTIFICATE_FAIL_MESSAGE =
  'An unexpected error occurred while migrating certificate mailed submissions.';

@Component({
  selector: 'app-energy-credit-support',
  imports: [
    MatTableModule,
    MatRow,
    MatCell,
    MatHeaderRow,
    MatHeaderCell,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
  ],
  templateUrl: './energy-credit-support.component.html',
  styleUrl: './energy-credit-support.component.scss',
})
export class EnergyCreditSupportComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private supportService = inject(SupportService);
  private adminService = inject(EnergyCreditsAdminService);
  private submissionsService = inject(SubmissionsService);
  private spinner = inject(SpinnerService);
  public dialog = inject(MatDialog);
  public dialogService = inject(DialogService);
  private router = inject(Router);

  facilities: FacilityEnergyCredit[] = [];
  selectedFacility: FacilityEnergyCredit | null = null;
  selectedMigrationFacility: FacilityEnergyCredit | null = null;
  facilityToResync: FacilityEnergyCredit | null = null;
  serialNumber = '';

  ngOnInit(): void {
    this.adminService
      .getFacilities()
      .pipe(takeUntil(this.destroy$))
      .subscribe((facilities) => {
        this.facilities = facilities
          .slice()
          .sort((a, b) =>
            a.KeyChain.LegacyId.localeCompare(b.KeyChain.LegacyId),
          );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayedColumns: string[] = ['task', 'action'];

  data = [
    {
      buttonLabel: 'Trigger',
      task: 'Trigger Energy Credit Submission Export',
      action: () => this.triggerSubmissionExport(),
    },
    {
      buttonLabel: 'Trigger',
      task: 'Trigger Heat Pump Invoice Sync',
      action: () => this.triggerHeatPumpInvoiceSync(),
    },
    {
      buttonLabel: 'Launch',
      task: 'Launch Energy Credits Facility Management',
      action: () => this.manageFacilityEligibility(),
    },
  ];

  triggerSubmissionExport() {
    this.supportService
      .getNumberOfExportableSubmissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        const formattedCount = new Intl.NumberFormat('en-us').format(count);
        const ref = this.dialogService.openDialog(
          'Confirm Submission Export',
          `This will submit approximately ${formattedCount} submissions for export. Do you want to proceed? This action cannot be undone.`,
          'Cancel',
          'Confirm',
          true,
        );
        ref
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((result) => {
            if (result) {
              this.spinner.show();
              this.supportService
                .triggerEnergyCreditSubmissionExport()
                .pipe(
                  finalize(() => this.spinner.hide()),
                  takeUntil(this.destroy$),
                )
                .subscribe();
            }
          });
      });
  }

  triggerHeatPumpInvoiceSync() {
    const ref = this.dialogService.openDialog(
      'Confirm Heat Pump Invoice Sync',
      `This will sync all submissions to OnBase to set their Heat Pump Invoice. Do you want to proceed? This action cannot be undone.`,
      'Cancel',
      'Confirm',
      true,
    );
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.spinner.show();
          this.supportService
            .triggerHeatPumpInvoiceSync()
            .pipe(
              finalize(() => this.spinner.hide()),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }
      });
  }
  triggerClaytonGoToNextDataSync() {
    if (!this.selectedFacility) return;
    const { Name, KeyChain } = this.selectedFacility;
    const ref = this.dialogService.openDialog(
      'Confirm ClaytonGO to NEXT Data Sync',
      `This will trigger the ClaytonGO to NEXT Data Sync for facility ${KeyChain.LegacyId} - ${Name}. Do you want to proceed? This action cannot be undone.`,
      'Cancel',
      'Confirm',
      true,
    );
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.spinner.show();
          this.supportService
            .triggerClaytonGoToNextDataSync(KeyChain.LegacyId)
            .pipe(
              finalize(() => this.spinner.hide()),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }
      });
  }
  resyncFacility() {
    if (!this.facilityToResync) return;
    const { Name, KeyChain } = this.facilityToResync;
    const ref = this.dialogService.openDialog(
      'Confirm Facility Resync',
      `This will delete the NEXT submissions for facility ${KeyChain.LegacyId} - ${Name} that have already been submitted in ClaytonGO so they can be re-synced. Do you want to proceed? This action cannot be undone.`,
      'Cancel',
      'Confirm',
      true,
    );
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed) this.executeResync(KeyChain.LegacyId);
      });
  }

  private executeResync(facilityNumber: string) {
    this.spinner.show();
    this.supportService
      .resyncFacility(facilityNumber)
      .pipe(
        finalize(() => this.spinner.hide()),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          const data = result.data?.['ResyncFacility'];
          this.dialogService.openDialog(
            data?.Success ? 'Facility Resync' : 'Facility Resync Failed',
            data?.Message ?? RESYNC_FACILITY_FAIL_MESSAGE,
            '',
            'Ok',
            false,
          );
        },
        error: () =>
          this.dialogService.openDialog(
            'Facility Resync Failed',
            RESYNC_FACILITY_FAIL_MESSAGE,
            '',
            'Ok',
            false,
          ),
      });
  }

  migrateCertificateMailedSubmissions() {
    if (!this.selectedMigrationFacility) return;
    const { Name, KeyChain } = this.selectedMigrationFacility;
    const ref = this.dialogService.openDialog(
      'Confirm Migrate Certificate Mailed Submissions',
      `This will migrate certificate mailed submissions for facility ${KeyChain.LegacyId} - ${Name}. Do you want to proceed? This action cannot be undone.`,
      'Cancel',
      'Confirm',
      true,
    );
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.spinner.show();
        this.supportService
          .migrateCertificateMailedSubmissions(KeyChain.LegacyId)
          .pipe(
            finalize(() => this.spinner.hide()),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (result) => {
              const data = result.data?.['MigrateCertificateMailedSubmissions'];
              this.dialogService.openDialog(
                data?.Success ? 'Migration Complete' : 'Migration Failed',
                data?.Message ?? MIGRATE_CERTIFICATE_FAIL_MESSAGE,
                '',
                'Ok',
                false,
              );
            },
            error: () =>
              this.dialogService.openDialog(
                'Migration Failed',
                MIGRATE_CERTIFICATE_FAIL_MESSAGE,
                '',
                'Ok',
                false,
              ),
          });
      });
  }

  refreshRetailer() {
    const serialNumber = this.serialNumber.trim();
    if (!serialNumber) return;
    this.spinner.show();
    this.submissionsService
      .refreshRetailerBySerialNumber(serialNumber)
      .pipe(
        finalize(() => this.spinner.hide()),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          const data = result.data?.['RefreshSubmissionRetailerBySerialNumber'];
          this.dialogService.openDialog(
            data?.Success ? 'Retailer Updated' : 'Retailer Not Updated',
            data?.Message ?? REFRESH_RETAILER_FAIL_MESSAGE,
            '',
            'Ok',
            false,
          );
          if (data?.Success) this.serialNumber = '';
        },
        error: () =>
          this.dialogService.openDialog(
            'Retailer Not Updated',
            REFRESH_RETAILER_FAIL_MESSAGE,
            '',
            'Ok',
            false,
          ),
      });
  }

  manageFacilityEligibility() {
    this.router.navigate(['administration/support/energy-credits-facilities']);
  }
}
